import { Seo } from "@/components/common/Seo";
import { PageHero } from "@/components/common/PageHero";
import { Reveal, RevealGroup, RevealItem } from "@/components/common/Reveal";
import { SectionLabel } from "@/components/common/SectionLabel";
import { CTAButton } from "@/components/common/CTAButton";
import { SEO, BRAND, VALUES, WHY_WINS } from "@/data/site";

const Story = () => (
  <section className="bg-paper pt-24 md:pt-32 pb-12 md:pb-16">
    <div className="container-px grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <Reveal><SectionLabel>Our Story</SectionLabel></Reveal>
      </div>
      <div className="lg:col-span-8">
        <Reveal>
          <p className="font-display font-700 text-2xl md:text-3xl leading-snug text-balance">
            AdX was born from a simple observation: the residential DOOH market in India is built on unfair contracts and opaque economics.
          </p>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="mt-8 space-y-5 text-ink/65 leading-relaxed max-w-2xl">
            <p>AdX is a DOOH advertising platform and a venture of {BRAND.parent} - an IT consulting and digital transformation company with offices in Kolkata, Bengaluru, and Dubai.</p>
            <p>Societies deserve better. Advertisers deserve better. We built AdX to deliver both: competitive fixed payouts and real transparency for communities, a self-serve marketplace and per-impression billing for brands, and protected community time on every screen.</p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Values = () => (
  <section className="bg-white pt-12 md:pt-16 pb-24 md:pb-32 border-y border-ink/10">
    <div className="container-px">
      <Reveal><SectionLabel>Our Values</SectionLabel></Reveal>
      <RevealGroup className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10 border border-ink/10 rounded-xl overflow-hidden">
        {VALUES.map((v, i) => (
          <RevealItem key={i} className="bg-paper p-8 md:p-10">
            <span className="font-display font-800 text-teal text-lg">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="mt-4 font-display font-700 text-2xl">{v.t}</h3>
            <p className="mt-3 text-ink/60 leading-relaxed">{v.d}</p>
          </RevealItem>
        ))}
      </RevealGroup>
    </div>
  </section>
);

const Parent = () => (
  <section className="bg-ink text-white py-24 md:py-28">
    <div className="container-px grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div>
        <SectionLabel dark>SevenM Technologies</SectionLabel>
        <h2 className="mt-6 font-display font-800 text-3xl md:text-4xl tracking-tight text-balance">
          Backed by a digital transformation company.
        </h2>
        <p className="mt-6 text-white/65 leading-relaxed max-w-lg">
          {BRAND.parent} is an IT consulting and digital transformation company with offices in Kolkata, Bengaluru, and UAE - and a portfolio of technology ventures including Admitek, PayMyFee, FASTagPro, Scanix and Engagr.
        </p>
      </div>
      <div className="grid grid-cols-3 gap-4">
        {["Kolkata", "Bengaluru", "Dubai"].map((c) => (
          <div key={c} className="border border-white/10 rounded-lg p-5 text-center">
            <p className="font-display font-700">{c}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WhyWins = () => (
  <section className="bg-white py-24 md:py-32 border-t border-ink/10">
    <div className="container-px">
      <Reveal><SectionLabel>Why AdX Wins</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-5xl tracking-tight max-w-3xl text-balance">
          Ten reasons, in plain terms.
        </h2>
      </Reveal>
      <RevealGroup className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-px">
        {WHY_WINS.map((w, i) => (
          <RevealItem key={i} className="flex gap-4 py-5 border-b border-ink/10">
            <span className="font-display font-800 text-teal tabular-nums">{String(i + 1).padStart(2, "0")}</span>
            <span className="text-ink/75">{w}</span>
          </RevealItem>
        ))}
      </RevealGroup>
      <Reveal delay={0.1}>
        <div className="mt-12"><CTAButton to="/contact" variant="dark">Get started with AdX</CTAButton></div>
      </Reveal>
    </div>
  </section>
);

export default function About() {
  return (
    <>
      <Seo title={SEO.about.title} description={SEO.about.description} />
      <PageHero
        label="About"
        titleLines={["Fairness, Made", "Operational."]}
        accentIndex={1}
        subtitle="Replacing the opaque, landlord-style DOOH model with a transparent, technology-driven platform that aligns communities, advertisers and residents."
      />
      <Story />
      <Values />
      <Parent />
      <WhyWins />
    </>
  );
}
